/**
 * Konfliktverwaltung: die Listenlogik hinter dem Konflikt-Banner und der Nachladewarteschlange.
 * Reine Funktionen, kein React - der Reduzierer setzt sie zusammen, `Speicher.tsx` fuehrt aus.
 *
 * Alle Funktionen liefern die UNVERAENDERTE Liste zurueck (dieselbe Instanz), wenn sich nichts
 * aendert. Das ist keine Mikrooptimierung: `Speicher.tsx` haengt einen Effekt an
 * `zustand.nachzuladen`; eine bei jedem Aufruf frisch kopierte Liste liesse ihn bei jedem
 * Zustandswechsel neu laufen und koennte denselben Datensatz endlos nachladen.
 */

/** Verweis auf genau einen Datensatz, entitaetsuebergreifend. */
export interface Datensatzverweis {
  entitaet: string;
  id: string;
}

/** Ein offener Konflikt: fremde Aenderung an einem Datensatz, den dieser Client geoeffnet hat. */
export interface Konflikt extends Datensatzverweis {
  /**
   * Nutzer-**id** des Aendernden, NICHT sein Anzeigename. Beide Quellen liefern eine id: das
   * SSE-Ereignis (`actor`, siehe `backend/src/sse/broadcast.ts` - dort ausdruecklich begruendet)
   * und die 409-Antwort (`current.updatedBy`, siehe `backend/src/http/routes/config.ts`). Die
   * Aufloesung zum Anzeigenamen passiert erst beim Rendern; der Banner bekommt sie als Prop.
   */
  geaendertVon: string;
  /**
   * Beim 409 der bereits mitgelieferte Serverstand (`current`) - damit "neu laden" ohne zweite
   * Anfrage auskommt. Beim SSE-Ereignis leer: dessen Nutzlast traegt keine Daten
   * (Global Constraint 13), dort loest "neu laden" ein gezieltes Nachladen aus.
   */
  serverstand?: unknown;
  /**
   * `true`, solange eine Uebernahme laeuft (Fehleranzeige Paket 2, Task 5): der Nutzer hat "neu laden" gedrueckt, das
   * Nachladen steht aus. Der Konflikt bleibt stehen, bis der aktuelle Stand wirklich geladen ist (`nachgeladen`); nur
   * dann loest die Nachladeantwort ihn. Eine Antwort ohne laufende Uebernahme (etwa die des eigenen Speicherns) tut es
   * nicht - der Nutzer hat nichts entschieden. Ein neues Fremdereignis ersetzt den Konflikt (`konfliktVormerken`) und
   * damit auch diese Marke: der Abruf lief vor der zweiten Aenderung los, sein Stand kann schon wieder alt sein.
   */
  uebernahmeLaeuft?: true;
}

/**
 * Zwei Verweise zeigen auf denselben Datensatz, wenn ENTITAET UND ID uebereinstimmen. Beides zu
 * pruefen ist tragend, nicht Gruendlichkeit: ids sind nur innerhalb ihrer Entitaet eindeutig, und
 * ein Vergleich nur ueber `id` liesse eine Lieferantenaenderung als Konflikt an einer gleichnamigen
 * Bestellung erscheinen.
 */
export function istDerselbeDatensatz(
  a: Datensatzverweis | null | undefined,
  b: Datensatzverweis,
): boolean {
  return !!a && a.entitaet === b.entitaet && a.id === b.id;
}

/**
 * Merkt einen Konflikt vor. Ein bereits offener Konflikt zum SELBEN Datensatz wird ERSETZT, nicht
 * verdoppelt: zwei Banner fuer dieselbe Bestellung waeren sinnlos, und der neuere Eintrag traegt
 * den aktuelleren Serverstand.
 */
export function konfliktVormerken(liste: Konflikt[], neu: Konflikt): Konflikt[] {
  const ohne = liste.filter((k) => !istDerselbeDatensatz(k, neu));
  return [...ohne, neu];
}

export function konfliktLoesen(liste: Konflikt[], verweis: Datensatzverweis): Konflikt[] {
  const ohne = liste.filter((k) => !istDerselbeDatensatz(k, verweis));
  return ohne.length === liste.length ? liste : ohne;
}

export function konfliktZu(liste: Konflikt[], verweis: Datensatzverweis): Konflikt | undefined {
  return liste.find((k) => istDerselbeDatensatz(k, verweis));
}

/**
 * Merkt am Konflikt zu `verweis`, dass seine Uebernahme laeuft. Ohne Konflikt oder bei bereits laufender Uebernahme
 * bleibt die Liste unveraendert (dieselbe Instanz): ein zweiter Klick waehrend des Abrufs aendert nichts.
 */
export function uebernahmeStarten(liste: Konflikt[], verweis: Datensatzverweis): Konflikt[] {
  const konflikt = konfliktZu(liste, verweis);
  if (!konflikt || konflikt.uebernahmeLaeuft) return liste;
  return liste.map((k) => (k === konflikt ? { ...k, uebernahmeLaeuft: true } : k));
}

/** Nimmt die Marke wieder weg (das Nachladen scheiterte): der Konflikt bleibt, ein neuer Klick startet neu. */
export function uebernahmeAbbrechen(liste: Konflikt[], verweis: Datensatzverweis): Konflikt[] {
  const konflikt = konfliktZu(liste, verweis);
  if (!konflikt || !konflikt.uebernahmeLaeuft) return liste;
  return liste.map((k) => {
    if (k !== konflikt) return k;
    const { uebernahmeLaeuft: _laeuft, ...ohneMarke } = k;
    return ohneMarke;
  });
}

/**
 * Der aktuelle Stand ist geladen: loest den Konflikt zu `verweis`, aber NUR bei laufender Uebernahme. Sonst bleibt die
 * Liste unveraendert (dieselbe Instanz).
 */
export function uebernahmeAbschliessen(liste: Konflikt[], verweis: Datensatzverweis): Konflikt[] {
  const konflikt = konfliktZu(liste, verweis);
  if (!konflikt || !konflikt.uebernahmeLaeuft) return liste;
  return konfliktLoesen(liste, verweis);
}

export function uebernahmeLaeuft(liste: Konflikt[], verweis: Datensatzverweis): boolean {
  return konfliktZu(liste, verweis)?.uebernahmeLaeuft === true;
}

/**
 * Merkt einen Nachladebedarf vor - hoechstens einmal je Datensatz. Ohne diese Entdopplung waechst
 * die Warteschlange bei jedem Tastendruck eines fremden Bearbeiters um einen Eintrag, und derselbe
 * Datensatz wird mehrfach nachgeladen.
 */
export function nachladenVormerken(
  liste: Datensatzverweis[],
  neu: Datensatzverweis,
): Datensatzverweis[] {
  if (liste.some((v) => istDerselbeDatensatz(v, neu))) return liste;
  return [...liste, { entitaet: neu.entitaet, id: neu.id }];
}

export function nachladenErledigt(
  liste: Datensatzverweis[],
  verweis: Datensatzverweis,
): Datensatzverweis[] {
  const ohne = liste.filter((v) => !istDerselbeDatensatz(v, verweis));
  return ohne.length === liste.length ? liste : ohne;
}
